import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'
import { verifyValue } from '@/common/utils/verifyHelper'
import { failsTextFieldFormatVerify } from '@/common/utils/textFieldFormat'
import { normalizeFormElementType } from '@/common/utils/formElementType'
import {
  buildSwingComboBoxFieldMapFromMergedData,
  parseComboBoxIdFieldPairsFromElement,
} from '@/common/utils/comboBoxSwingFields'
import { ElementContextKey, FormProviderKey, SwingTilesRowScopeKey } from '@/components/platform/injection-keys'
import { coreService } from '@/services/core/core.service'
import type { FormPath } from '@/services/core/types'
import { resolveEffectiveElementFormId } from '@/common/utils/resolveEffectiveElementFormId'
import { findPageStoreEntryForForm } from '@/common/utils/resolvePageStoreEntry'
import { useAppStore } from '@/stores/app'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, inject, onMounted, onUnmounted, ref, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
import { usePageData } from './usePageData'
import { profilePayload, startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields'
import { mergePageFieldMaps, overlayAuthoritativeFieldKeys } from '@/common/utils/mergePageFieldMaps'
import { isAuthoritativeUniDataTable } from '@/common/utils/pageDataFieldMerge'
import {
  isFormOverlayPageId,
  overlayWidgetUsesRegistrySlot,
  shouldSkipOverlaySharedPageFields,
} from '@/common/utils/formOverlayPageScope'
import {
  resolveElementInitialEditable,
  resolveElementInitialVisible,
  coerceStateBoolean,
} from '@/common/utils/resolveActionPageState'
import {
  isPageFieldScalarElementType,
  resolveElementPageFieldScalar,
} from '@/common/utils/resolveElementPageFieldScalar'
import {
  readBooleanFromFieldEntry,
  resolveBooleanFieldValueFromPageFields,
} from '@/common/utils/resolveBooleanFieldValue'
import { mergeLiveFieldOverridesIntoFlatFormData, isTextLikeFormElementType } from '@/common/utils/liveFormFieldOverrides'
import { readElementWithCheckBoxes } from '@/common/utils/tableLeafGetData'
import { applyDateRangeToClientDataFieldsFromRegistry } from '@/common/utils/elementGetDataExport'
import {
  buildInlineTableReloadPayload,
  filterReloadWidePoolByScope,
  isInlineTableReloadContext,
} from '@/common/utils/filterReloadPayloadByScope'
import {
  logAttachmentStoreDebug,
} from '@/common/utils/attachmentDebugLog'
import {
  markAttachmentStoreMutationCause,
} from '@/common/utils/attachmentStoreMutationCause'
import { fieldEntryHasPresentableValue } from '@/common/utils/changedDataFieldMerge'
import { extractListboxScalarValue, isListboxEmptyValue } from '@/common/utils/resolveListboxFieldValue'
import { buildReloadElementMergeScopeKeys } from '@/common/utils/reloadElementMergeScope'
import {
  normalizeReloadTableFieldsMap,
  normalizeUniDataTableEmptyFlag,
} from '@/common/utils/normalizeReloadTableEmptyFlag'
import { isolateAllSharedTableFieldValues } from '@/common/utils/cloneTableValue'
import { setTypes } from '@/common/utils/typedValueSerializer'
import {
  isCollateralScalarFilterFieldEntry,
  resolveRefillSkipStore,
} from '@/common/utils/refillElementStoreSync'
import { toMultiSelectTypedValue } from '@/common/utils/normalizeMultiselectTableValue'
import { pickElementEventScript } from '@/common/utils/scriptNodeText'
import { invokeOnChangeAfterTextRefill } from '@/common/utils/textRefillOnChange'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import {
  emptyUniDataTableField,
  buildTreeTableSearchProcedureContext,
  flatExpandContextFromPageFields,
  isFullTreeReloadIntent,
  isLazyTreeTableRowExpandIntent,
  isTreeTableSearchReloadPayload,
  mergeFullTreeReloadIntoFlatFormData,
  shouldOmitPageScalarForTreeTableExpand,
} from '@/common/utils/treeTableReloadNodePayload'

function primitiveFieldsFromSnapshot(fields: Record<string, any> | null | undefined): Record<string, any> {
  const out: Record<string, any> = {}
  if (!fields || typeof fields !== 'object') return out
  for (const [key, fieldData] of Object.entries(fields)) {
    if (key.includes('__listbox') || key.includes('__action') || key.includes('__page')) continue
    if (fieldData && typeof fieldData === 'object' && 'value' in fieldData && 'type' in fieldData) {
      if (fieldData.type === 'UniDataTable') continue
      const fieldValue = fieldData.value
      if (
        fieldValue === null ||
        fieldValue === undefined ||
        typeof fieldValue === 'string' ||
        typeof fieldValue === 'number' ||
        typeof fieldValue === 'boolean'
      ) {
        out[key] = fieldValue
      }
    } else if (
      fieldData === null ||
      fieldData === undefined ||
      typeof fieldData === 'string' ||
      typeof fieldData === 'number' ||
      typeof fieldData === 'boolean'
    ) {
      out[key] = fieldData
    }
  }
  return out
}

interface ElementContext {
  formId: string
  panelId?: string
  elementId?: number
  isReadOnly?: boolean
  jsName?: string
}

interface ElementConfig {
  element: {
    _attributes?: {
      id?: string
    }
    type: { _text: string }
    name?: { _text: string }
    description?: { _text: string }
    format?: { _text: string }
    [key: string]: any
  }
  _attributes: Record<string, any>
}

interface ReloadOptions {
  applyResult?: boolean
  source?: string
  skipOnElement?: boolean
  skipPageFocusGuard?: boolean
  pageDataSlot?: string
}

interface ReloadedOptions {
  skipOnElement?: boolean
  skipEnvelopeSetValue?: boolean
  fieldNames?: string[]
  skipClientData?: boolean
  source?: string
  pageDataSlot?: string
}

/** Парсинг boolean из узла формы */
function parseOptionalXmlBool(node: { _text?: string } | undefined | null, defaultVal: boolean): boolean {
  if (node == null || node._text === undefined || node._text === null) return defaultVal
  const t = String(node._text).trim().toLowerCase()
  if (t === '') return defaultVal
  return t === 'true' || t === '1' || t === 'yes'
}

interface ElementHooks {
  getSelected?: () => any
  getContext?: () => any
  getSuffixes?: () => string[]
  reload?: (out?: any, options?: ReloadOptions) => Promise<any>
  onReloaded?: (data: any, options?: ReloadedOptions) => void
  getAutoCompleteList?: (options?: {
    filter?: string
    cursor?: number
    forced?: boolean
    fieldOverlay?: Record<string, unknown>
  }) => Promise<any[]>
  refill?: (data: any) => void
  empty?: () => void
  invokeEvent?: (eventName: string, data?: any) => void | Promise<void>
  setEditable?: (isEditable: boolean) => void
  setVisible?: (isVisible: boolean) => void
  setHasEditor?: (hasEditor: boolean) => void
  receiveFocus?: () => void
  verify?: () => boolean
  scrollIntoViewElement?: (name:string) => void
  expandAll?: () => void
  reloadNode?: (rowNum?: unknown, needReloadParent?: unknown, selectedNodeData?: unknown) => Promise<any>
  buildReloadNodePayload?: (rowNum: number, needReloadParent: boolean) => Record<string, any>
  setMultipleLimit?: (limit: number) => void
  setAutocompleteMode?: (mode: string) => void
  setEnableSuggestEdit?: (enabled: boolean) => void
  setMultipleDelimiter?: (delimiter: string) => void
  setVerifyRegexp?: (regexp: string) => void
  /** SwingTiles.getData — только для корня Tiles */
  getData?: (_action?: unknown) => unknown
  /** SwingTiles.setInlineVisibility */
  setInlineVisibility?: (a: boolean | string, b?: boolean) => void
}

/**
 * Композабл для работы с элементами форм платформы
 */
export function useElement(config: ElementConfig, formPath: FormPath, readonly = false) {
  const { pageData, pageFields, pageActions, pageDataSlot, pageRegistryFormId, pageWindowInstanceId, pageStates } = usePageData()
  const formProvider = inject(FormProviderKey, null)
  const elementContext = inject(ElementContextKey, {} as ElementContext)
  const swingTilesRow = inject(SwingTilesRowScopeKey, null)
  const appStore = useAppStore()
  const { currentPlatform } = storeToRefs(appStore)
  const route = useRoute()

  const isElementXmlReadOnly = (value: unknown): boolean => {
    if (value == null) return false
    const normalized = String(value).trim().toLowerCase()
    return normalized === '1' || normalized === 'true'
  }

  const resolveElementIdentity = () => {
    const el = config.element
    const rawId = el?._attributes?.id
    const parsedId = rawId != null ? parseInt(String(rawId), 10) : NaN
    const elementId = Number.isFinite(parsedId) ? parsedId : 0
    const attrs = el?._attributes as { id?: string; name?: string } | undefined
    const elementName =
      elementContext?.jsName ||
      el?.name?._text ||
      (typeof attrs?.name === 'string' ? attrs.name : '') ||
      ''
    return { elementId, elementName: String(elementName) }
  }

  const resolveInitialEditable = (): boolean => {
    const { elementId, elementName } = resolveElementIdentity()
    const defaultEditable = !(readonly || isElementXmlReadOnly(config.element?.isReadOnly?._text))
    return resolveElementInitialEditable(
      pageStates.value,
      elementId,
      elementName,
      defaultEditable,
    )
  }

  const resolveInitialVisible = (): boolean => {
    const { elementId, elementName } = resolveElementIdentity()
    return resolveElementInitialVisible(pageStates.value, elementId, elementName, true)
  }

  const selected = ref<any[]>([])
  const editable = ref(resolveInitialEditable())
  const isVisible = ref(resolveInitialVisible())
  const hasEditor = ref(false)

  const syncElementShellVisible = (visible: boolean) => {
    if (swingTilesRow) return
    const { elementId } = resolveElementIdentity()
    if (elementId > 0) {
      formProvider?.setElementShellVisibleById?.(elementId, visible)
    }
  }
  if (isVisible.value === false) {
    syncElementShellVisible(false)
  }
  const focused = ref(false)
  const verificationError = ref<string | null>(null)

  // Computed properties
  const element = computed(() => config.element || null)
  const id = computed(() => element.value?._attributes?.id || null)
  const type = computed(() => normalizeFormElementType(element.value) ?? '')
  const name = computed(() => {
    if (elementContext?.jsName) {
      return elementContext.jsName
    }
    return element.value?.name?._text || element.value?._attributes?.id || ''
  })
  const fieldName = computed(() => {
    const el = element.value
    const attrs = el?._attributes as { id?: string; name?: string } | undefined
    const actualFieldName =
      el?.name?._text ||
      (typeof attrs?.name === 'string' ? attrs.name : null) ||
      attrs?.id ||
      ''
    return String(actualFieldName ?? '')
  })
  const label = computed(() => element.value?.description?._text || '')
  const format = computed(() => element.value?.format?._text)
  const suggestOnFocusGained = computed(() =>
    parseOptionalXmlBool(element.value?.suggestOnFocusGained, false),
  )
  const linked = computed(() => parseOptionalXmlBool(element.value?.linked, false))
  const attrs = computed(() => config._attributes)
  const canEmitOnInit = computed(() => value.value !== null)
  const verifyRegexp = computed(
    () =>
      verifyRegexpOverride.value ??
      element.value?.verifyRegexp?._text ??
      element.value?.verify_re?._text ??
      '',
  )
  const verifyErrorMessage = computed(
    () =>
      element.value?.verifyError?._text ??
      element.value?.verify_error?._text ??
      'Validation failed',
  )

  const registrationFieldName = computed(() =>
    swingTilesRow
      ? `${swingTilesRow.tableFieldName}__row${swingTilesRow.rowIndex}__${fieldName.value}`
      : fieldName.value,
  )

  const tilesRowScopeParam = () => (swingTilesRow ? { tilesRowScope: swingTilesRow } : {})

  const value = computed(() => {
    if (swingTilesRow) {
      return swingTilesRow.getRowFieldValue(fieldName.value)
    }
    if (!formProvider) {
      console.warn('FormProvider not available for value computation')
      return null
    }
    const formPathForValue = getEffectiveFormPath()
    const form = formProvider.getForm(formPathForValue)
    const overlayPageId = formPathForValue.pageId
    const overlayPage = isFormOverlayPageId(overlayPageId)
    const skipSharedPageFields = shouldSkipOverlaySharedPageFields(
      overlayPageId,
      fieldName.value,
      formProvider.getOverlayDuplicatedFieldNames?.(
        String(formPathForValue.formId ?? ''),
      ),
    )

    const possibleFieldNames = [
      fieldName.value,
      `${fieldName.value}__listbox`,
      `${fieldName.value}__items`,
      `${fieldName.value}__action`
    ]

    const pickOverlayRegistryEntry = () => {
      if (!overlayPage || !form) return undefined
      for (const testFieldName of possibleFieldNames) {
        const entry = form[testFieldName]
        if (overlayWidgetUsesRegistrySlot(overlayPageId, entry)) return entry
      }
      return undefined
    }

    const unwrapTableLikeEntry = (entry: unknown) => {
      if (
        entry &&
        typeof entry === 'object' &&
        !Array.isArray(entry) &&
        'value' in entry
      ) {
        return (entry as { value?: unknown }).value
      }
      return entry
    }

    const elTypeUpper = type.value?.toUpperCase()
    if (elTypeUpper === 'LISTBOX' || elTypeUpper === 'COMBOBOX') {
      const listboxFieldName = fieldName.value
      const formEntryValue = form?.[listboxFieldName]?.value
      const scalarFrom = (entry: unknown): string | number | boolean | '' => {
        const scalar = extractListboxScalarValue(entry)
        if (typeof scalar === 'string' || typeof scalar === 'number' || typeof scalar === 'boolean') {
          return scalar
        }
        return ''
      }
      const overlaySlot = pickOverlayRegistryEntry()
      if (overlaySlot !== undefined) {
        const fromForm = scalarFrom(overlaySlot)
        return isListboxEmptyValue(fromForm) ? null : fromForm
      }
      if (skipSharedPageFields) {
        const fromForm = scalarFrom(formEntryValue)
        return isListboxEmptyValue(fromForm) ? null : fromForm
      }
      const pageEntry = (pageFields.value as Record<string, any> | undefined)?.[listboxFieldName]
      const fromPage = scalarFrom(pageEntry)
      if (!isListboxEmptyValue(fromPage)) {
        return fromPage
      }
      const fromForm = scalarFrom(formEntryValue)
      return isListboxEmptyValue(fromForm) ? null : fromForm
    }

    if (isPageFieldScalarElementType(type.value)) {
      const overlaySlot = pickOverlayRegistryEntry()
      if (overlaySlot !== undefined) {
        const scalar = unwrapScriptValue(
          overlaySlot && typeof overlaySlot === 'object' && 'value' in overlaySlot
            ? (overlaySlot as { value?: unknown }).value
            : overlaySlot,
        )
        if (scalar != null && String(scalar).trim() !== '') return scalar
        return null
      }
      if (skipSharedPageFields) return null
      for (const testFieldName of possibleFieldNames) {
        const entry = form?.[testFieldName]
        if (entry?.value === undefined) continue
        const scalar = unwrapScriptValue(entry.value)
        if (scalar != null && String(scalar).trim() !== '') {
          return scalar
        }
      }
      return resolveElementPageFieldScalar(
        pageFields.value as Record<string, unknown> | undefined,
        fieldName.value,
      )
    }

    const isTextLike = isTextLikeFormElementType(type.value)
    const isTableLike =
      type.value === 'TABLE' ||
      type.value === 'TREETABLE' ||
      type.value === 'EDITABLE' ||
      type.value === 'GROUPTABLE' ||
      type.value === 'FILEATTACHMENTS'

    if (isTextLike) {
      const overlaySlot = pickOverlayRegistryEntry()
      if (overlaySlot !== undefined) {
        const raw =
          overlaySlot && typeof overlaySlot === 'object' && 'value' in overlaySlot
            ? (overlaySlot as { value?: unknown }).value
            : overlaySlot
        return unwrapScriptValue(raw)
      }
      // skipSharedPageFields: не pageFields родителя, свой слот всё равно читаем ниже.
    }

    if (isTableLike) {
      const overlaySlot = pickOverlayRegistryEntry()
      if (overlaySlot !== undefined) {
        return unwrapTableLikeEntry(overlaySlot)
      }
      if (skipSharedPageFields) return null
      for (const testFieldName of possibleFieldNames) {
        const pageEntry = (pageFields.value as Record<string, any> | undefined)?.[testFieldName]
        if (!isAuthoritativeUniDataTable(pageEntry)) continue
        if (
          pageEntry &&
          typeof pageEntry === 'object' &&
          !Array.isArray(pageEntry) &&
          'value' in pageEntry
        ) {
          return pageEntry.value
        }
        return pageEntry
      }
    }

    for (const testFieldName of possibleFieldNames) {
      const entry = form?.[testFieldName]
      if (entry?.value === undefined) continue
      if (isTextLike) {
        if (
          entry != null &&
          typeof entry === 'object' &&
          'value' in entry &&
          (entry as { value?: unknown }).value !== undefined
        ) {
          return unwrapScriptValue(entry.value)
        }
        continue
      }
      if (type.value === 'FILEATTACHMENTS' && extractUniDataTableRows(entry).length === 0) {
        continue
      }
      if (type.value === 'BOOLEAN') {
        const fromEntry = readBooleanFromFieldEntry(entry)
        if (fromEntry !== null) return fromEntry
        continue
      }
      return entry.value
    }

    if (isTextLike && !skipSharedPageFields) {
      const pageEntry = (pageFields.value as Record<string, any> | undefined)?.[fieldName.value]
      if (pageEntry && typeof pageEntry === 'object' && !Array.isArray(pageEntry) && 'value' in pageEntry) {
        const raw = pageEntry.value
        if (raw !== undefined && raw !== null) return unwrapScriptValue(raw)
      }
      if (fieldEntryHasPresentableValue(pageEntry)) {
        return pageEntry
      }
    }

    if (isTableLike) {
      for (const testFieldName of possibleFieldNames) {
        const pageEntry = (pageFields.value as Record<string, any> | undefined)?.[testFieldName]
        if (extractUniDataTableRows(pageEntry).length > 0) {
          if (
            pageEntry &&
            typeof pageEntry === 'object' &&
            !Array.isArray(pageEntry) &&
            'value' in pageEntry
          ) {
            return pageEntry.value
          }
          return pageEntry
        }
      }
    }

    if (type.value === 'BOOLEAN') {
      const fromPage = resolveBooleanFieldValueFromPageFields(
        pageFields.value as Record<string, unknown> | undefined,
        fieldName.value,
      )
      if (fromPage !== null) return fromPage
    }

    if (type.value === 'DATE' || type.value === 'DATETIME') {
      const pageEntry = (pageFields.value as Record<string, any> | undefined)?.[fieldName.value]
      if (pageEntry !== undefined) {
        const raw =
          pageEntry && typeof pageEntry === 'object' && !Array.isArray(pageEntry) && 'value' in pageEntry
            ? pageEntry.value
            : pageEntry
        // Явный clear (value: null / "") — не оставлять предыдущую дату из registry.
        if (raw === undefined || raw === null || raw === '') {
          return null
        }
        return raw
      }
    }

    return null
  })

  const stubRegister = () => console.warn('FormProvider not available - register skipped')
  const stubUnregister = () => console.warn('FormProvider not available - unregister skipped')
  const stubInput = () => console.warn('FormProvider not available - input skipped')
  const stubOnReloaded = (_data?: any, _options?: ReloadedOptions) => console.warn('FormProvider not available - onReloaded skipped')
  const stubRunEvent = () => console.warn('FormProvider not available - runEvent skipped')
  const stubRefill = () => console.warn('FormProvider not available - refill skipped')
  const stubReload = async (_out?: any, _options?: ReloadOptions) => {
    console.warn('FormProvider not available - reload skipped')
    return undefined
  }
  const stubInvokeEvent = () => console.warn('FormProvider not available - invokeEvent skipped')
  const stubOnElement = () => console.warn('FormProvider not available - onElement skipped')
  const stubRunCode = () => console.warn('FormProvider not available - runCode skipped')
  const stubRunAction = () => console.warn('FormProvider not available - runAction skipped')
  const stubRunActionByName = () => console.warn('FormProvider not available - runActionByName skipped')
  const refillOnChangeRunning = { current: false }

  const getEffectiveFormPath = () => {
    const ctxFormId = (elementContext as any)?.formId ?? formPath.formId
    const xmlElementId = id.value
    const pathElementId = formPath.elementId
    const resolvedElementId =
      pathElementId != null && String(pathElementId).trim() !== '' && Number(pathElementId) !== -1
        ? pathElementId
        : xmlElementId != null && Number(xmlElementId) > 0
          ? xmlElementId
          : pathElementId
    const resolvedPageDataSlot =
      String(pageDataSlot.value ?? '').trim() ||
      String((formPath as any)?.pageDataSlot ?? '').trim() ||
      undefined
    return {
      ...formPath,
      panelId: (elementContext as any)?.panelId ?? (formPath as any).panelId,
      formId: resolveEffectiveElementFormId({
        contextFormId: ctxFormId,
        pageRegistryFormId: pageRegistryFormId.value,
        pageDataSlot: resolvedPageDataSlot,
      }),
      pageId: (elementContext as any)?.pageId ?? (formPath as any).pageId,
      pageDataSlot: resolvedPageDataSlot,
      elementId: resolvedElementId,
    }
  }

  // Track registration so we can re-register when formId changes
  let lastRegisterArgs: { elementName: string; initialValue: any; hooksToOverride: ElementHooks } | null = null
  let lastRegisteredFormId: string | number | null = null

  const register = (elementName: string, initialValue: any, hooksToOverride: ElementHooks = {}) => {
    if (!formProvider) {
      console.warn('❌ FormProvider not found - cannot register element')
      stubRegister()
      return
    }

    const effectivePath = getEffectiveFormPath()
    const resolvedName = swingTilesRow
      ? `${swingTilesRow.tableFieldName}__row${swingTilesRow.rowIndex}__${elementName}`
      : elementName
    const registerData = {
      value: initialValue,
      name: resolvedName,
      element: element.value,
      type: type.value,
      elementId: effectivePath.elementId,
      panelId: (effectivePath as any).panelId,
      formId: effectivePath.formId,
      ctx: getContext(),
      canEmitOnInit: canEmitOnInit.value,
      hooks: {
        getSelected,
        getContext,
        getSuffixes,
        reload,
        refill,
        empty,
        invokeEvent,
        setEditable,
        setVisible,
        setHasEditor,
        setError,
        removeTextFieldBorder,
        setCursorPositionToEnd,
        setVerifyRegexp,
        receiveFocus,
        verify,
        ...hooksToOverride
      }
    }

    formProvider.registerElement(registerData)
    lastRegisterArgs = { elementName, initialValue, hooksToOverride }
    lastRegisteredFormId = effectivePath.formId ?? null
  }

  const unregister = () => {
    if (!formProvider) {
      stubUnregister()
      return
    }
    formProvider.unRegisterElement({
      ...getEffectiveFormPath(),
      fieldName: registrationFieldName.value,
    })
  }

  const hasRequiredRegistrationPath = (path = getEffectiveFormPath()) => {
    const formIdValue = String(path.formId ?? '').trim()
    const panelIdValue = path.panelId
    const elementIdValue = path.elementId
    if (!formIdValue || formIdValue === '-1') return false
    if (panelIdValue == null || panelIdValue === '' || Number(panelIdValue) === -1) return false
    if (elementIdValue == null || elementIdValue === '' || Number(elementIdValue) === -1) return false
    return true
  }

  const isRegisteredInProvider = () => {
    if (!formProvider?.getElements) return false
    const path = getEffectiveFormPath()
    const regName = registrationFieldName.value
    if (!regName) return false
    const entry = formProvider.getElements()?.[regName] as Record<string, unknown> | undefined
    if (!entry) return false
    return (
      String(entry.formId ?? '') === String(path.formId ?? '') &&
      String(entry.panelId ?? '') === String(path.panelId ?? '') &&
      String(entry.elementId ?? '') === String(path.elementId ?? '')
    )
  }

  const tryRegisterWhenReady = () => {
    if (!lastRegisterArgs || !formProvider) return
    if (!hasRequiredRegistrationPath()) return
    if (isRegisteredInProvider()) return
    register(
      lastRegisterArgs.elementName,
      lastRegisterArgs.initialValue,
      lastRegisterArgs.hooksToOverride,
    )
  }

  const clearCheckboxTableSelection = () => {
    const elType = String(type.value ?? '').toUpperCase()
    if (elType !== 'TABLE' && elType !== 'EDITABLE' && elType !== 'TREETABLE') return
    if (!readElementWithCheckBoxes(element.value)) return
    const registerSelection = formProvider?.registerTableSelection
    if (typeof registerSelection !== 'function') return
    const path = getEffectiveFormPath()
    if (path.formId == null || path.elementId == null) return
    const fn = String(fieldName.value ?? '').trim()
    if (!fn) return
    registerSelection(path.formId, (path as { panelId?: string | number }).panelId, path.elementId, fn, [])
  }

  // Re-register when formId/elementId появляются (dialog SAME_WINDOW / поздний path).
  watch(
    () => {
      const path = getEffectiveFormPath()
      return [
        String(path.formId ?? ''),
        String(path.elementId ?? ''),
        String(path.panelId ?? ''),
        String(pageRegistryFormId.value ?? ''),
        String(pageDataSlot.value ?? ''),
      ].join('|')
    },
    (next, prev) => {
      if (!lastRegisterArgs || !formProvider) return
      if (next === prev) return
      if (!hasRequiredRegistrationPath()) return
      register(
        lastRegisterArgs.elementName,
        lastRegisterArgs.initialValue,
        lastRegisterArgs.hooksToOverride,
      )
    },
  )

  watch(
    () => id.value,
    () => {
      hasEditor.value = parseOptionalXmlBool(element.value?.hasEditor, false)
    },
    { immediate: true },
  )

  const _input = (value: any, options?: { skipOnElement?: boolean; skipStore?: boolean }) => {
    if (!formProvider) {
      stubInput()
      return
    }

    if (swingTilesRow) {
      swingTilesRow.commitRowField(fieldName.value, value)
      if (!options?.skipOnElement) {
        formProvider.onElement(getContext())
      }
      return
    }

    const path = getEffectiveFormPath()
    let valuePayload: Record<string, any> = { [fieldName.value]: value }
    let omitKeys: string[] | undefined

    if (type.value === 'FILEATTACHMENTS') {
      markAttachmentStoreMutationCause({
        fieldName: fieldName.value,
        elementId: path.elementId,
        cause: 'user-input',
        detail: options?.skipStore ? { skipStore: true } : undefined,
      })
    }

    const elTypeUpper = normalizeFormElementType(element.value)?.toUpperCase()
    if (elTypeUpper === 'COMBOBOX') {
      const pairs = parseComboBoxIdFieldPairsFromElement(element.value)
      if (pairs.length && fieldName.value) {
        omitKeys = [`${fieldName.value}__text`]
        const swing = buildSwingComboBoxFieldMapFromMergedData(fieldName.value, element.value, (k) => {
          if (k === fieldName.value) {
            return value
          }
          return (pageFields.value as Record<string, any> | undefined)?.[k]
        })
        if (swing) {
          valuePayload = { ...swing }
        }
      }
    }

    formProvider.setValue({
      ...path,
      value: valuePayload,
      ...(omitKeys ? { omitKeys } : {}),
      ...(options?.skipStore ? { skipStore: true } : {}),
      ...tilesRowScopeParam(),
    })

    if (!options?.skipOnElement) {
      formProvider.onElement({
        ...getContext(),
        elementForm: setTypes(valuePayload),
      })
    }
  }

  const onReloaded = (data: any, options: ReloadedOptions = {}) => {
    if (!formProvider) {
      stubOnReloaded()
      return
    }

    const fieldNames = Array.isArray(options.fieldNames)
      ? options.fieldNames.map((field) => String(field || '').trim()).filter(Boolean)
      : []
    const restrictFields = fieldNames.length > 0
    const sourceFields = normalizeReloadTableFieldsMap(data?.fields?.fields || {})
    const fieldsToApply = restrictFields
      ? fieldNames.reduce<Record<string, any>>((acc, field) => {
        if (Object.prototype.hasOwnProperty.call(sourceFields, field)) {
          acc[field] = sourceFields[field]
        }
        return acc
      }, {})
      : sourceFields

    const fieldNameForReload = fieldName.value
    const reloadedFieldRaw = fieldsToApply?.[fieldNameForReload]
    const reloadedField = reloadedFieldRaw
      ? normalizeUniDataTableEmptyFlag(reloadedFieldRaw)
      : reloadedFieldRaw
    if (fieldNameForReload && reloadedField) {
      fieldsToApply[fieldNameForReload] = reloadedField
    }

    const resolvedPageSlot =
      String((options as { pageDataSlot?: string } | undefined)?.pageDataSlot ?? '').trim() ||
      String(pageDataSlot.value ?? '').trim() ||
      undefined

    // Все таблицы из ответа — в registry+store
    const registryTablePatch: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(fieldsToApply || {})) {
      if (
        entry &&
        typeof entry === 'object' &&
        !Array.isArray(entry) &&
        String((entry as { type?: string }).type ?? '') === 'UniDataTable'
      ) {
        registryTablePatch[key] = normalizeUniDataTableEmptyFlag(entry)
      }
    }
    if (fieldNameForReload && reloadedField) {
      registryTablePatch[fieldNameForReload] = reloadedField
    }

    const shouldSyncPageDataAfterReload = Boolean(
      data &&
        (Object.keys(fieldsToApply || {}).length > 0 ||
          (!options.skipClientData && data.clientData?.params)),
    )

    if (Object.keys(registryTablePatch).length > 0) {
      if (type.value === 'FILEATTACHMENTS' && fieldNameForReload) {
        markAttachmentStoreMutationCause({
          fieldName: fieldNameForReload,
          elementId: getEffectiveFormPath().elementId,
          cause: 'element-reload',
          detail: { source: options?.source ?? 'unknown' },
        })
      }
      const isTableReloadField =
        Boolean(fieldNameForReload) &&
        Boolean(reloadedField) &&
        typeof reloadedField === 'object' &&
        !Array.isArray(reloadedField) &&
        String((reloadedField as { type?: string }).type ?? '') === 'UniDataTable'
      // Для таблиц — одна запись в финальной sync-фазе ниже (после pageData merge).
      if (!(shouldSyncPageDataAfterReload && isTableReloadField)) {
        formProvider.setValue({
          ...getEffectiveFormPath(),
          value: registryTablePatch,
          pageDataSlot: resolvedPageSlot,
          ...tilesRowScopeParam(),
        })
      }
    }

    const shouldSyncReloadedTableToRegistry = Object.keys(registryTablePatch).length > 0

    if (data?.clientData && !options.skipClientData) {
      const clientData = data.clientData
      const elementName = name.value

      // Apply element conditions for visibility
      if (clientData.elementConditions) {
        let conditionValue: boolean | null = null

        // Check by element name
        if (clientData.elementConditions[elementName] !== undefined) {
          conditionValue = Boolean(clientData.elementConditions[elementName])
        } else {
          // Check by tag name + ID (if element has an ID)
          const elementId = element.value?._attributes?.id
          if (elementId) {
            const elementType = type.value.toLowerCase() // 'element', 'panel'...
            const altKey = `${elementType}__${elementId}`
            if (clientData.elementConditions[altKey] !== undefined) {
              conditionValue = Boolean(clientData.elementConditions[altKey])
            }
          }
        }

        // Set visibility (ve.setVisible(!b))
        if (conditionValue !== null && setVisible) {
          setVisible(!conditionValue)
        }
      }

      // Apply read-only state from params
      // Check for elementName + "__IS_READ_ONLY"
      if (clientData.params) {
        const readOnlyKey = `${elementName}__IS_READ_ONLY`
        if (readOnlyKey in clientData.params) {
          const isReadOnly = coerceStateBoolean(clientData.params[readOnlyKey]) === true
          // Set editable (ve.setEditable(!Boolean.TRUE.equals(...)))
          if (setEditable) {
            setEditable(!isReadOnly)
          }
        }
      }
    }

    if (shouldSyncPageDataAfterReload) {
      const effectivePath = getEffectiveFormPath()
      const routeIdRaw = route?.params?.id
      const routePageId = Array.isArray(routeIdRaw) ? routeIdRaw[0] : routeIdRaw
      // En/dialog: явный pageDataSlot — писать туда, не в «угаданный» storeKey.
      const slotKey = String(resolvedPageSlot || pageDataSlot.value || '').trim()
      const slotPage =
        slotKey !== '' ? (appStore.pagesData as Record<string, any>)[slotKey] : undefined
      const resolved = slotPage
        ? { storeKey: slotKey, page: slotPage }
        : findPageStoreEntryForForm(appStore.pagesData as Record<string, any>, {
            routeParamId: routePageId != null && routePageId !== '' ? String(routePageId) : null,
            currentPageId: appStore.currentPageId,
            slotId: slotKey || undefined,
            registryFormId: pageRegistryFormId.value || effectivePath.formId,
            windowInstanceId: pageWindowInstanceId.value,
            topDialogId: appStore.dialogIds?.length
              ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
              : '',
            formId: effectivePath.formId,
          })
      const fallbackKey =
        effectivePath.formId != null && effectivePath.formId !== ''
          ? String(effectivePath.formId)
          : ''
      const pageStoreKey = resolved?.storeKey ?? (fallbackKey !== '' ? fallbackKey : '')
      const currentPageData =
        resolved?.page ??
        (fallbackKey !== '' ? (appStore.pagesData as Record<string, any>)[fallbackKey] : undefined)

      if (currentPageData && pageStoreKey !== '') {
        const nextClientData = options.skipClientData
          ? currentPageData.uniData?.clientData
          : {
            ...currentPageData.uniData?.clientData,
            elementConditions: data.clientData?.elementConditions || currentPageData.uniData?.clientData?.elementConditions || null,
            params: {
              ...currentPageData.uniData?.clientData?.params,
              ...(data.clientData?.params || {})
            }
          }

        const reloadScopeKeys = buildReloadElementMergeScopeKeys(
          fieldNameForReload,
          fieldNames,
          fieldsToApply || {},
        )

        const mergeOptions = {
          shouldPreserveEmptyIncoming: (key: string) => !reloadScopeKeys.has(key),
        }
        let mergedUniFields = isolateAllSharedTableFieldValues(
          mergePageFieldMaps(
            mergeOptions,
            currentPageData.uniData?.fields?.fields,
            fieldsToApply || {},
          ) as Record<string, unknown>,
        )
        let mergedChangedFields = isolateAllSharedTableFieldValues(
          mergePageFieldMaps(
            mergeOptions,
            currentPageData.jsResult?.changed_data?.fields?.fields,
            fieldsToApply || {},
          ) as Record<string, unknown>,
        )
        // Все UniDataTable из ответа reload 
        const authoritativeTables: Record<string, unknown> = {}
        for (const [key, entry] of Object.entries(fieldsToApply || {})) {
          if (
            entry &&
            typeof entry === 'object' &&
            !Array.isArray(entry) &&
            String((entry as { type?: string }).type ?? '') === 'UniDataTable'
          ) {
            authoritativeTables[key] = normalizeUniDataTableEmptyFlag(entry)
          }
        }
        if (fieldNameForReload && reloadedField) {
          authoritativeTables[fieldNameForReload] = reloadedField
        }
        if (Object.keys(authoritativeTables).length > 0) {
          mergedUniFields = isolateAllSharedTableFieldValues(
            overlayAuthoritativeFieldKeys(mergedUniFields, authoritativeTables),
          )
          mergedChangedFields = isolateAllSharedTableFieldValues(
            overlayAuthoritativeFieldKeys(mergedChangedFields, authoritativeTables),
          )
        }

        const updatedPageData = {
          ...currentPageData,
          uniData: {
            ...currentPageData.uniData,
            clientData: nextClientData,
            fields: {
              ...currentPageData.uniData?.fields,
              fields: mergedUniFields,
            },
          },
          jsResult: {
            ...(currentPageData.jsResult || {}),
            changed_data: {
              ...(currentPageData.jsResult?.changed_data || {}),
              fields: {
                ...(currentPageData.jsResult?.changed_data?.fields || {}),
                fields: mergedChangedFields,
              },
            },
          },
          // Иначе mergePageDataFieldSnapshot поднимает stale rows из chain поверх reload.
          currentChainData: (() => {
            const chain = currentPageData.currentChainData
            if (!chain || typeof chain !== 'object') return chain
            const chainFields = chain.fields?.fields
            if (!chainFields || typeof chainFields !== 'object') return chain
            if (Object.keys(authoritativeTables).length === 0) return chain
            return {
              ...chain,
              fields: {
                ...(chain.fields || {}),
                fields: {
                  ...chainFields,
                  ...authoritativeTables,
                },
              },
            }
          })(),
        }

        appStore.setPageData(
          pageStoreKey,
          updatedPageData,
          undefined,
          undefined,
          options.source ? `useElement.onReloaded:${options.source}` : 'useElement.onReloaded',
        )

        const mergedFields = updatedPageData.uniData?.fields?.fields as Record<string, unknown> | undefined
        const refillElement = (formProvider as { refillElement?: (name: string, data: unknown, formId?: string) => void })
          .refillElement
        if (mergedFields && typeof refillElement === 'function') {
          // Сначала — перезагруженные UniDataTable (UI мог не подхватить setValue sibling).
          for (const [key, entry] of Object.entries(authoritativeTables)) {
            try {
              refillElement(key, entry, String(effectivePath.formId ?? ''))
            } catch {
              /* non-fatal UI sync */
            }
          }
          for (const [key, entry] of Object.entries(mergedFields)) {
            if (reloadScopeKeys.has(key)) continue
            if (!isCollateralScalarFilterFieldEntry(entry, key)) continue
            try {
              refillElement(key, entry, String(effectivePath.formId ?? ''))
            } catch {
              /* non-fatal UI sync */
            }
          }
        }
      }
    }

    if (shouldSyncReloadedTableToRegistry) {
      formProvider.setValue({
        ...getEffectiveFormPath(),
        value: registryTablePatch,
        skipStore: true,
        pageDataSlot: resolvedPageSlot,
        ...tilesRowScopeParam(),
      })
    }

    // Trigger onChange action (ve.doOnChangeAction(false))
    if (formProvider && !options.skipOnElement) {
      formProvider.onElement(getContext())
    }
    // Swing ReloadHelper: fill then doOnChangeAction. skipOnElement only skips onElement (TEXT no-op).
    invokeOnChangeAfterTextRefill({
      elementType: type.value,
      onChangeScript: pickElementEventScript(element.value, 'onChange'),
      invokeEvent,
      running: refillOnChangeRunning,
      elementName: resolveElementIdentity().elementName,
    })
  }

  const runEvent = async (event: any, eventName?: string, context?: any) => {
    if (!formProvider) {
      stubRunEvent()
      return
    }

    const evName = String(eventName ?? '')
    const isDropLike = evName === 'onDrop' || evName === 'canImport'
    const selectedData =
      context != null && typeof context === 'object' && 'selectedData' in context
        ? context.selectedData
        : isDropLike
          ? context
          : context?.selectedData

    await formProvider.runAction({
      data: event,
      level: 'element',
      eventName: eventName,
      selectedData,
      limitToSelectedData: Boolean(
        selectedData && (context?.selectedIdx != null || context?.column != null),
      ),
      ...getEffectiveFormPath(),
    })
  }

  const getContext = () => {
    const formPath = getEffectiveFormPath()
    return {
      pageData: pageData.value,
      pageFields: pageFields.value,
      pageActions: pageActions.value,
      pageDataSlot: pageDataSlot.value,
      element: element.value,
      formPath,
      formId: formPath.formId,
      panelId: formPath.panelId,
      elementId: formPath.elementId,
      selected: selected.value,
      editable: editable.value,
      isVisible: isVisible.value,
      focused: focused.value,
      ...(swingTilesRow ? { tilesRowScope: swingTilesRow } : {}),
    }
  }

  const getSelected = () => selected.value

  const getSuffixes = (): string[] => {
    return [""]
  }

  const refill = (data: any) => {
    if (!formProvider) {
      stubRefill()
      return
    }

    try {
      const rawData = data ? toRaw(data) : data

      let fieldValue: any = undefined

      if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
        const dataKeys = Object.keys(rawData)
        const lookupKey =
          (fieldName.value && fieldName.value in rawData ? fieldName.value : null) ||
          (name.value && name.value in rawData ? name.value : null) ||
          (dataKeys.length === 1 ? dataKeys[0] : null)

        if (lookupKey) {
          const fieldData = rawData[lookupKey]
          const rawFieldData = fieldData ? toRaw(fieldData) : fieldData
          const isMultiSelect = normalizeFormElementType(type.value) === 'MULTISELECT'

          if (isMultiSelect && rawFieldData !== undefined) {
            fieldValue = toMultiSelectTypedValue(rawFieldData)
          } else if (rawFieldData && typeof rawFieldData === 'object' && 'value' in rawFieldData) {
            fieldValue = {
              value: unwrapScriptValue(rawFieldData.value),
              type: rawFieldData.type || 'String'
            }
          } else {
            fieldValue = {
              value: unwrapScriptValue(rawFieldData),
              type: typeof rawFieldData === 'string' ? 'String' :
                typeof rawFieldData === 'number' ? 'Long' :
                  typeof rawFieldData === 'boolean' ? 'Boolean' : 'String'
            }
          }
        }
      }

      const setKey = String(fieldName.value || name.value || '')
      const valueToSet = fieldValue !== undefined && setKey ? { [setKey]: fieldValue } : {}

      const effectivePath = getEffectiveFormPath()
      if (type.value === 'FILEATTACHMENTS' && setKey) {
        markAttachmentStoreMutationCause({
          fieldName: setKey,
          elementId: effectivePath.elementId,
          cause: 'element-refill',
        })
      }
      const setValueParams = {
        formId: effectivePath.formId,
        panelId: (effectivePath as any).panelId,
        elementId: effectivePath.elementId,
        value: valueToSet,
        skipStore: resolveRefillSkipStore(type.value, fieldValue),
        ...tilesRowScopeParam(),
      }

      formProvider.setValue(setValueParams)

      clearCheckboxTableSelection()

      // Swing ReloadHelper: fill then doOnChangeAction. TEXT onChange is not onElement.
      invokeOnChangeAfterTextRefill({
        elementType: type.value,
        onChangeScript: pickElementEventScript(element.value, 'onChange'),
        invokeEvent,
        running: refillOnChangeRunning,
        elementName: resolveElementIdentity().elementName,
      })
    } catch (error) {
      console.error("[useElement.refill] Error in refill function:", error)
      throw error
    }
  }

  const reload = async (out?: any, options?: ReloadOptions) => {
    if (!formProvider) {
      return stubReload(out, options)
    }

    const { formId } = getEffectiveFormPath()
    const panelId = (getEffectiveFormPath() as any).panelId

    const endMeasure = startWorkflowMeasure('useElement.reload', {
      name: name.value,
      fieldName: fieldName.value,
      formId,
      panelId,
      elementId: id.value,
      source: options?.source || 'unknown',
      payload: profilePayload(out),
    })

    const endGetFormMeasure = startWorkflowMeasure('useElement.reload.getForm', {
      name: name.value,
      formId,
      panelId,
    })
    // En.slotAware передаёт pageDataSlot в options — приоритетнее inject FormProvider.
    const reloadPageSlot =
      String(options?.pageDataSlot ?? '').trim() ||
      String(pageDataSlot.value ?? '').trim() ||
      undefined
    const panelForm = formProvider.getForm({
      formId,
      panelId,
      flatten: true,
      pageDataSlot: reloadPageSlot,
    }) || {}
    const fullForm = formProvider.getForm({
      formId,
      flatten: true,
      pageDataSlot: reloadPageSlot,
    }) || {}
    // SAME_WINDOW dialog
    const reloadPageEntry =
      (reloadPageSlot ? appStore.pagesData[reloadPageSlot] : undefined) ??
      (formId != null ? appStore.pagesData[formId] : undefined) ??
      pageData.value
    const pageSnapshot = primitiveFieldsFromSnapshot(
      mergePageDataFieldSnapshot(reloadPageEntry),
    )
    endGetFormMeasure()

    let sessionId = (reloadPageEntry as any)?.sessionId
    if (!sessionId || (typeof sessionId === 'number' && sessionId < 0)) {
      const appSessionId = (pageData.value as any)?.sessionId
      if (appSessionId && !(typeof appSessionId === 'number' && appSessionId < 0)) {
        sessionId = appSessionId
      }
    }
    const isTreeTableSearchReload =
      Boolean(fieldName.value) &&
      (options?.source === 'tabRoutedTreeTableSearch' ||
        isTreeTableSearchReloadPayload(fieldName.value, out as Record<string, unknown>))

    const lazyRowExpandOnly =
      out &&
      fieldName.value &&
      !isTreeTableSearchReload &&
      (options?.source === 'ElementRenderer.treeLazyExpand' ||
        isLazyTreeTableRowExpandIntent(fieldName.value, out))

    const slimPayloadOnly = lazyRowExpandOnly || isTreeTableSearchReload

    const tableFieldName = fieldName.value
    const outForInlineDetect = (
      out && typeof out === 'object' && !Array.isArray(out) ? out : {}
    ) as Record<string, unknown>
    const isInlineReload =
      Boolean(tableFieldName) &&
      !slimPayloadOnly &&
      isInlineTableReloadContext(tableFieldName, outForInlineDetect)

    const preserveTableFields: string[] = []

    if (out && typeof out === 'object' && !Array.isArray(out)) {
      for (const [key, value] of Object.entries(out)) {
        if (!value || typeof value !== 'object') continue
        if (key === fieldName.value && !lazyRowExpandOnly) continue
        const fieldType = String((value as { type?: string }).type ?? '')
        const fieldValue = (value as { value?: { rows?: unknown[] } }).value
        if (
          fieldType === 'UniDataTable' ||
          fieldType === 'UniDataTableFields' ||
          Array.isArray((value as { rows?: unknown[] }).rows) ||
          Array.isArray(fieldValue?.rows)
        ) {
          preserveTableFields.push(key)
        }
      }
    }

    const effectivePath = getEffectiveFormPath()
    const elementIdNum = Number(effectivePath.elementId)
    const enhancedPath: FormPath = {
      formId: String(effectivePath.formId ?? ''),
      panelId:
        effectivePath.panelId != null && String(effectivePath.panelId).trim() !== ''
          ? String(effectivePath.panelId)
          : undefined,
      pageDataSlot: effectivePath.pageDataSlot,
      elementId: Number.isFinite(elementIdNum) ? elementIdNum : -1,
      sessionId,
      type: currentPlatform.value ?? undefined,
      fieldName: fieldName.value,
      preserveTableFields,
    }

    try {
      const liveOverrides = formProvider.collectLiveFieldOverrides?.(formId) ?? {}

      const pageEntry = reloadPageEntry
      const chainScalars = primitiveFieldsFromSnapshot(
        (pageEntry as any)?.currentChainData?.fields?.fields,
      )
      const panelScalars = primitiveFieldsFromSnapshot(panelForm as Record<string, unknown>)

      let mergedFormData: Record<string, unknown>
      if (slimPayloadOnly) {
        mergedFormData = { ...(out as Record<string, unknown>) }
      } else if (isInlineReload && tableFieldName) {
        mergedFormData = buildInlineTableReloadPayload(
          outForInlineDetect,
          tableFieldName,
          pageSnapshot,
          primitiveFieldsFromSnapshot(fullForm as Record<string, unknown>),
          panelScalars,
          chainScalars,
        )
      } else {
        const outBase = { ...(out as Record<string, unknown>) }
        const widePool: Record<string, unknown> = { ...pageSnapshot, ...fullForm, ...panelForm }
        const filteredWide = filterReloadWidePoolByScope(widePool, outBase, fieldName.value || '')
        mergedFormData = { ...filteredWide, ...outBase }
      }

      if (lazyRowExpandOnly && tableFieldName) {
        const expandPageFields = mergePageFieldMaps(panelScalars, chainScalars)
        const expandLiveOverrides = Object.fromEntries(
          Object.entries(liveOverrides).filter(
            ([key, value]) =>
              !shouldOmitPageScalarForTreeTableExpand(key, value, {
                tableName: tableFieldName,
                pageFields: expandPageFields,
                liveOverrides,
              }),
          ),
        )
        mergedFormData = mergeLiveFieldOverridesIntoFlatFormData(
          {
            ...flatExpandContextFromPageFields(expandPageFields, tableFieldName, liveOverrides),
            ...(out as Record<string, unknown>),
          },
          expandLiveOverrides,
        )
        delete mergedFormData[tableFieldName]
      } else if (isTreeTableSearchReload && tableFieldName) {
        mergedFormData = mergeLiveFieldOverridesIntoFlatFormData(
          {
            ...buildTreeTableSearchProcedureContext(panelScalars, chainScalars, tableFieldName, liveOverrides),
            ...(out as Record<string, unknown>),
          },
          liveOverrides,
        )
        delete mergedFormData[tableFieldName]
      }

      if (
        tableFieldName &&
        isFullTreeReloadIntent(tableFieldName, {
          ...(out && typeof out === 'object' && !Array.isArray(out) ? out : {}),
          ...mergedFormData,
        })
      ) {
        mergedFormData = mergeFullTreeReloadIntoFlatFormData(tableFieldName, mergedFormData)
      }

      // Не эхоить stale rows перезагружаемой таблицы: поле уходит без body
      // (core.service preserve path.fieldName не восстановит отсутствующий ключ).
      if (tableFieldName && !lazyRowExpandOnly) {
        delete mergedFormData[tableFieldName]
      }

      // SwingDateRange: UI {from,to} → wire __first/__second до createSafeClientData.
      applyDateRangeToClientDataFieldsFromRegistry(
        mergedFormData,
        { ...pageSnapshot, ...fullForm, ...panelForm, ...liveOverrides },
        typeof formProvider.getElements === 'function' ? formProvider.getElements() : undefined,
        (params) => formProvider.getForm({ ...params, flatten: true }) || {},
      )

      const endCoreReloadMeasure = startWorkflowMeasure('useElement.reload.coreReload', {
        name: name.value,
        formId,
        panelId,
        elementId: id.value,
        sessionId,
        source: options?.source || 'unknown',
        payload: profilePayload(mergedFormData),
      })
      const result = await coreService.reloadElement(enhancedPath, mergedFormData)
      endCoreReloadMeasure()

      const applyResult = options?.applyResult !== false
      if (applyResult) {
        const endApplyMeasure = startWorkflowMeasure('useElement.reload.applyResult', {
          name: name.value,
          formId,
          panelId,
          elementId: id.value,
          hasFields: Boolean(result?.fields),
          payload: profilePayload(result),
        })
        const reloadSlotOpt =
          String(options?.pageDataSlot ?? '').trim() ||
          String(pageDataSlot.value ?? '').trim() ||
          undefined
        if (result && result.fields && result.fields.fields && Object.keys(result.fields.fields).length > 0) {
          onReloaded(result, {
            skipOnElement: options?.skipOnElement === true,
            source: options?.source,
            pageDataSlot: reloadSlotOpt,
          })
        } else {
          if (result && result.formError && result.formError.length > 0) {
            console.error(`Form error for element ${name.value}:`, result.formError)
          }

          if (result && result.exception) {
            console.error(`Exception for element ${name.value}:`, result.exception)
          }

          onReloaded(result, {
            skipOnElement: options?.skipOnElement === true,
            source: options?.source,
            pageDataSlot: reloadSlotOpt,
          })
        }
        endApplyMeasure()
        clearCheckboxTableSelection()
      }

      return result
    } catch (error) {
      console.error(`Failed to reload element ${name.value}:`, error)
      return undefined
    } finally {
      endMeasure()
    }
  }

  const stubGetAutoCompleteList = async (): Promise<any[]> => {
    console.warn('FormProvider not available - getAutoCompleteList skipped')
    return []
  }

  const getAutoCompleteList = async (
    options: {
      filter?: string
      cursor?: number
      forced?: boolean
      fieldOverlay?: Record<string, unknown>
    } = {},
  ): Promise<any[]> => {
    if (!formProvider) return stubGetAutoCompleteList()

    const effectivePath = getEffectiveFormPath()
    const { formId } = effectivePath
    const panelId = (effectivePath as any).panelId
    const routeIdRaw = route?.params?.id
    const routePageId = Array.isArray(routeIdRaw) ? routeIdRaw[0] : routeIdRaw
    const resolved = findPageStoreEntryForForm(appStore.pagesData as Record<string, any>, {
      routeParamId: routePageId != null && routePageId !== '' ? String(routePageId) : null,
      currentPageId: appStore.currentPageId,
      slotId: pageDataSlot.value,
      registryFormId: pageRegistryFormId.value || effectivePath.formId,
      windowInstanceId: pageWindowInstanceId.value,
      topDialogId: appStore.dialogIds?.length
        ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
        : '',
      formId: effectivePath.formId,
    })
    const fallbackKey =
      effectivePath.formId != null && effectivePath.formId !== ''
        ? String(effectivePath.formId)
        : ''
    const pageStoreKey = resolved?.storeKey ?? (fallbackKey !== '' ? fallbackKey : '')
    const currentPageData =
      resolved?.page ??
      (fallbackKey !== '' ? (appStore.pagesData as Record<string, any>)[fallbackKey] : undefined) ??
      pageData.value

    const currentFormData = currentPageData?.uniData?.fields?.fields || {}
    const form = formProvider.getForm({
      formId,
      panelId,
      pageDataSlot: String(pageDataSlot.value ?? '').trim() || undefined,
    }) || {}
    const mergedFormData = { ...currentFormData, ...form }
    if (options.fieldOverlay && typeof options.fieldOverlay === 'object') {
      Object.assign(mergedFormData, options.fieldOverlay)
    }

    let sessionId = (currentPageData as any)?.sessionId ?? (pageData.value as any)?.sessionId
    if (!sessionId || (typeof sessionId === 'number' && sessionId < 0)) {
      const appSessionId =
        pageStoreKey !== ''
          ? (appStore.pagesData as Record<string, any>)[pageStoreKey]?.sessionId
          : (appStore.pagesData[formId] as any)?.sessionId
      if (appSessionId && !(typeof appSessionId === 'number' && appSessionId < 0)) {
        sessionId = appSessionId
      }
    }
    const elementIdNum = Number(effectivePath.elementId)
    const enhancedPath: FormPath = {
      formId: String(effectivePath.formId ?? ''),
      panelId:
        effectivePath.panelId != null && String(effectivePath.panelId).trim() !== ''
          ? String(effectivePath.panelId)
          : undefined,
      pageDataSlot: effectivePath.pageDataSlot,
      elementId: Number.isFinite(elementIdNum) ? elementIdNum : -1,
      sessionId,
      type: currentPlatform.value ?? undefined,
    }

    // Бэкенд ожидает имя поля (name._text), не id и не jsName — иначе не найдёт elementName__alternatives
    const elementNameForAutocomplete = fieldName.value

    try {
      return await coreService.getAutoCompleteList(
        enhancedPath,
        elementNameForAutocomplete,
        mergedFormData,
        options
      )
    } catch (error) {
      console.error(`Failed to get autoComplete list for element ${elementNameForAutocomplete}:`, error)
      return []
    }
  }

  const invokeEvent = async (eventName: string, data?: any) => {

    if (!formProvider) {
      stubInvokeEvent()
      return
    }
    if (eventName === 'inlineMenuShow') {
      eventName = 'onInlineMenuShow'
    }

    const selectedData = data?.selectedData ?? data?.selected ?? data
    ;(formProvider as any).setActionData?.(data)

    if (element.value?.[eventName]) {
      const result = await formProvider.runCode(
        element.value[eventName]._text,
        selectedData ?? getSelected(),
        {
          formId: formPath.formId,
          panelId: formPath.panelId,
          pageDataSlot: formPath.pageDataSlot,
          preloadPageFields: true,
        }
      )

      return result
    }

    const path = getEffectiveFormPath()
    const eventAction = (formProvider as any).getEventActionForElement?.(path.formId, path.elementId, eventName)
    if (eventAction) {
      return await formProvider.runAction({
        data: eventAction,
        level: 'element',
        eventName,
        formId: path.formId,
        panelId: path.panelId,
        elementId: path.elementId,
        selectedData,
      })
    }
    return formProvider.runActionByName(eventName)
  }

  const receiveFocus = () => {
    focused.value = true
  }

  const removeFocus = () => {
    focused.value = false
  }

  const setEditable = (isEditable = true) => {
    editable.value = isEditable
  }

  const setVisible = (isVisibleParam: boolean) => {
    isVisible.value = isVisibleParam
    syncElementShellVisible(isVisibleParam)
  }

  const setHasEditor = (nextHasEditor = true) => {
    hasEditor.value = nextHasEditor
  }

  const empty = () => {
    if (!formProvider) {
      console.warn('FormProvider not available - empty skipped')
      return
    }

    const key = fieldName.value
    const elType = type.value
    const isTableLike =
      elType === 'TABLE' ||
      elType === 'TREETABLE' ||
      elType === 'EDITABLE' ||
      elType === 'GROUPTABLE'
    const emptyValue = isTextLikeFormElementType(elType)
      ? { type: 'String', value: '' }
      : isTableLike || elType === 'FILEATTACHMENTS'
        ? emptyUniDataTableField()
        : null

    if (elType === 'FILEATTACHMENTS') {
      markAttachmentStoreMutationCause({
        fieldName: key,
        elementId: id.value,
        cause: 'element-empty',
      })
      logAttachmentStoreDebug('element-empty', {
        fieldName: key,
        emptyValue:
          emptyValue == null
            ? null
            : typeof emptyValue === 'object' && emptyValue !== null && 'type' in emptyValue
              ? { type: (emptyValue as { type?: unknown }).type }
              : emptyValue,
      })
    }

    formProvider.setValue({
      ...getEffectiveFormPath(),
      value: {
        [key]: emptyValue,
      },
      ...tilesRowScopeParam(),
    })
  }


  const setError = (message: string | null) => {
    verificationError.value = message
  }

  const borderRemoved = ref(false)

  const removeTextFieldBorder = () => {
    borderRemoved.value = true
  }

  const cursorToEnd = ref(false)

  const setCursorPositionToEnd = () => {
    cursorToEnd.value = true
  }

  /** null — без лимита */
  const multipleLimit = ref<number | null>(null)

  const setMultipleLimit = (limit: number) => {
    multipleLimit.value = Number.isFinite(limit) ? limit : null
  }

  /** null — брать из метаданных формы */
  const autocompleteModeOverride = ref<string | null>(null)
  const enableSuggestEditOverride = ref<string | null>(null)
  const multipleDelimiterOverride = ref<string | null>(null)
  const verifyRegexpOverride = ref<string | null>(null)

  const setAutocompleteMode = (mode: string) => {
    const m = mode != null ? String(mode).trim() : ''
    autocompleteModeOverride.value = m === '' ? null : m
  }

  const setEnableSuggestEdit = (enabled: boolean) => {
    enableSuggestEditOverride.value = enabled ? 'true' : 'false'
  }

  const setMultipleDelimiter = (delimiter: string) => {
    const d = delimiter != null ? String(delimiter) : ''
    multipleDelimiterOverride.value = d === '' ? null : d
  }

  const setVerifyRegexp = (regexp: string) => {
    const r = regexp != null ? String(regexp).trim() : ''
    verifyRegexpOverride.value = r === '' ? null : r
  }

  const verify = (): boolean => {
    verificationError.value = null

    if (!editable.value || !isVisible.value) {
      return true
    }

    const regexp = verifyRegexp.value
    const hasRegexp = Boolean(regexp && regexp !== 'false' && regexp.trim() !== '')
    const verifiableString = getVerifiableString()
    const elementType = String(type.value ?? '').trim().toUpperCase()
    const isTextLikeFormat =
      elementType === 'TEXT' || elementType === 'TEXTAREA' || elementType === 'MULTILINETEXT'

    if (hasRegexp) {
      const result = verifyValue(verifiableString, regexp, verifyErrorMessage.value)
      if (!result.isValid) {
        verificationError.value = result.errorMessage || 'Validation failed'
        return false
      }
    }

    if (isTextLikeFormat && failsTextFieldFormatVerify(format.value, verifiableString)) {
      verificationError.value = verifyErrorMessage.value || 'Validation failed'
      return false
    }

    return true
  }

  const getVerifiableString = (): string => {
    const currentValue = value.value
    const elementType = type.value

    // Handle different element types like Swing client does
    switch (elementType) {
      case 'TEXT':
      case 'PASSWORD':
      case 'TEXTAREA':
      case 'MULTILINETEXT':
        // For text inputs, validate the text itself
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'LISTBOX':
      case 'COMBOBOX':
        // For selectors, validate the selected item's display value
        // In Swing, this is "code=X;description=Y;" but we'll use the display text
        if (currentValue && typeof currentValue === 'object') {
          // If value is an object with text/label property
          return currentValue.text || currentValue.label || String(currentValue.value || '')
        }
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'DATETIME':
      case 'DATE':
        // For dates, validate the formatted date string
        if (currentValue instanceof Date) {
          // Format as DD.MM.YYYY like Swing client
          const day = String(currentValue.getDate()).padStart(2, '0')
          const month = String(currentValue.getMonth() + 1).padStart(2, '0')
          const year = currentValue.getFullYear()
          return `${day}.${month}.${year}`
        }
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'DATERANGE':
        // For date ranges, validate both dates
        if (currentValue && typeof currentValue === 'object') {
          const from = currentValue.from instanceof Date
            ? formatDateForVerification(currentValue.from)
            : ''
          const to = currentValue.to instanceof Date
            ? formatDateForVerification(currentValue.to)
            : ''
          return `${from}-${to}`
        }
        return ''

      case 'TABLE':
      case 'EDITABLE':
      case 'TREETABLE':
        // For tables, validate the row count like Swing client
        if (currentValue && typeof currentValue === 'object' && Array.isArray(currentValue.rows)) {
          return String(currentValue.rows.length)
        }
        return '0'

      case 'MULTISELECT':
        // For multiselect, validate the selected items as comma-separated string
        if (Array.isArray(currentValue)) {
          return currentValue.map(item =>
            typeof item === 'object' ? (item.text || item.label || item.value) : String(item)
          ).join(',')
        }
        return ''

      case 'BOOLEAN':
      case 'CHECKBOX':
        // For boolean/checkbox, validate as "true"/"false" string
        return String(Boolean(currentValue))

      case 'SLIDER':
        // For slider, validate the numeric value as string
        return String(currentValue || 0)

      case 'PROGRESSBAR':
        // For progress bar, validate the current value
        return String(currentValue || 0)

      case 'IDENTIFICATOR':
        // For identificator (hidden field), validate the value as string
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'STRINGRANGE':
        // For string range, validate as "relation:first-second"
        if (currentValue && typeof currentValue === 'object') {
          const relation = currentValue.relation || '='
          const first = currentValue.first || ''
          const second = currentValue.second || ''
          return `${relation}:${first}${second ? '-' + second : ''}`
        }
        return ''

      case 'HTML':
        // For HTML, validate the raw HTML content
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'DIRECTORY':
        // For directory, validate the path string
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'FILEUPLOAD':
      case 'EXTFILEUPLOAD':
        // For file upload, validate the file name
        if (currentValue && typeof currentValue === 'object' && 'name' in currentValue) {
          return currentValue.name
        }
        return ''

      case 'FILEDOWNLOAD':
      case 'EXTFILEDOWNLOAD':
        // For file download, validate the file name
        if (currentValue && typeof currentValue === 'object' && 'fileName' in currentValue) {
          return currentValue.fileName
        }
        return ''

      case 'TILES':
        if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
          const r = (currentValue as { rows?: unknown[] }).rows
          if (Array.isArray(r)) return String(r.length)
        }
        if (Array.isArray(currentValue)) {
          return String(currentValue.length)
        }
        return '0'

      case 'SVG':
        // For SVG, validate the content (could validate if it's valid SVG)
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'GROUPTABLE':
        // For grouped table, validate the row count
        if (currentValue && typeof currentValue === 'object' && Array.isArray(currentValue.rows)) {
          return String(currentValue.rows.length)
        }
        return '0'

      case 'TASKS':
        // For tasks, validate the number of tasks
        if (Array.isArray(currentValue)) {
          return String(currentValue.length)
        }
        return '0'

      case 'APPROVAL':
        // For approval, validate the number of approval groups
        if (Array.isArray(currentValue)) {
          return String(currentValue.length)
        }
        return '0'

      case 'MEDIAWALL':
        // For media wall, validate the number of media items
        if (Array.isArray(currentValue)) {
          return String(currentValue.length)
        }
        return '0'

      case 'VIDEOPLAYER':
        // For video player, validate the video URL
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''

      case 'DOWNLOADTASK':
        // For download task, validate the number of files
        if (Array.isArray(currentValue)) {
          return String(currentValue.length)
        }
        return '0'

      default:
        // Default: convert to string
        return currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : ''
    }
  }

  const formatDateForVerification = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  // Auto-unregister when component is unmounted
  // Only register lifecycle hook if we're in a valid component instance
  const instance = getCurrentInstance()
  if (instance) {
    onMounted(() => {
      tryRegisterWhenReady()
    })
    onUnmounted(() => {
      if (formProvider) {
        unregister()
      }
    })
  }

  return {
    selected,
    editable,
    isVisible,
    hasEditor,
    suggestOnFocusGained,
    linked,
    focused,
    verificationError,
    element,
    id,
    type,
    name,
    fieldName,
    label,
    format,
    attrs,
    canEmitOnInit,
    value,
    verifyRegexp,
    verifyErrorMessage,
    register: formProvider ? register : stubRegister,
    unregister: formProvider ? unregister : stubUnregister,
    _input: formProvider ? _input : stubInput,
    onReloaded: formProvider ? onReloaded : stubOnReloaded,
    runEvent: formProvider ? runEvent : stubRunEvent,
    getContext,
    getSelected,
    getSuffixes,
    refill: formProvider ? refill : stubRefill,
    reload: formProvider ? reload : stubReload,
    getAutoCompleteList: formProvider ? getAutoCompleteList : stubGetAutoCompleteList,
    invokeEvent: formProvider ? invokeEvent : stubInvokeEvent,
    receiveFocus,
    removeFocus,
    setEditable,
    setVisible,
    setHasEditor,
    empty,
    verify,
    borderRemoved,
    removeTextFieldBorder,
    cursorToEnd,
    setCursorPositionToEnd,
    multipleLimit,
    setMultipleLimit,
    autocompleteModeOverride,
    enableSuggestEditOverride,
    multipleDelimiterOverride,
    verifyRegexpOverride,
    setAutocompleteMode,
    setEnableSuggestEdit,
    setMultipleDelimiter,
    setVerifyRegexp,
  }
}
